import { useEffect, useState } from "react";
import { api } from "../lib/api";
const ACTION_TYPES = [
    "SETTLEMENT_DISCOUNT",
    "WRITE_OFF",
    "CASE_WITHDRAWAL",
    "CASE_CLOSURE_WITH_OUTSTANDING",
    "CHEQUE_RETURN",
    "RECEIVABLE_AMOUNT_CHANGE",
    "CASE_CANCELLATION",
];
// SETTLEMENT_DISCOUNT bands are basis points (percent x100); everything else is
// minor units of QAR (money x100). The form asks for percent/QAR and converts
// before sending - the API only ever sees the stored unit.
function isPercentAction(actionType) {
    return actionType === "SETTLEMENT_DISCOUNT";
}
// The standalone app picked an approver ROLE per step. Here Management is the
// `approve` action on the Legal Cases card, so a step either goes to any
// Management user or to the specific people named on it.
export function AdminApprovalHierarchy() {
    const [rules, setRules] = useState([]);
    const [managers, setManagers] = useState([]);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [actionType, setActionType] = useState(ACTION_TYPES[0]);
    const [minAmount, setMinAmount] = useState("");
    const [maxAmount, setMaxAmount] = useState("");
    const [sequence, setSequence] = useState("1");
    const [approverUserIds, setApproverUserIds] = useState([]);
    function load() {
        api.get("/admin/approval-rules").then((r) => setRules(r.items)).catch(() => setError("Couldn't load approval rules."));
        api.get("/users?roleName=MANAGEMENT").then((r) => setManagers(r.items)).catch(() => { });
    }
    useEffect(load, []);
    const nameOf = (id) => managers.find((m) => m.id === id)?.name ?? "(no longer Management)";
    const approversLabel = (ids) => (ids && ids.length ? ids.map(nameOf).join(", ") : "Any Management user");
    function displayAmount(value, type) {
        if (value === null || value === undefined)
            return "—";
        return isPercentAction(type) ? `${(value / 100).toFixed(2)}%` : `QAR ${(value / 100).toLocaleString()}`;
    }
    async function create() {
        setSaving(true);
        setError(null);
        try {
            const toStoredUnit = (v) => (v === "" ? null : Math.round(Number(v) * 100));
            await api.post("/admin/approval-rules", {
                actionType,
                minAmount: toStoredUnit(minAmount),
                maxAmount: toStoredUnit(maxAmount),
                sequence: Number(sequence),
                approverUserIds,
                isMandatory: true,
            });
            setMinAmount("");
            setMaxAmount("");
            setSequence("1");
            setApproverUserIds([]);
            setShowForm(false);
            load();
        }
        catch (e) {
            setError(e?.message && e.status !== 0 ? e.message : "Couldn't save this rule.");
        }
        finally {
            setSaving(false);
        }
    }
    async function remove(id) {
        if (!window.confirm("Remove this approval step?")) return;
        try {
            await api.delete(`/admin/approval-rules/${id}`);
            load();
        }
        catch {
            setError("Couldn't delete this rule.");
        }
    }
    const toggleApprover = (id) => setApproverUserIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
    const grouped = ACTION_TYPES.map((type) => ({
        type,
        rules: rules.filter((r) => r.actionType === type).sort((a, b) => (a.minAmount ?? -1) - (b.minAmount ?? -1) || a.sequence - b.sequence),
    }));
    return (<div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h1 style={{ fontWeight: 600, fontSize: "1.6rem", margin: 0 }}>Approval Rules</h1>
        <button onClick={() => setShowForm((v) => !v)} style={btnStyle}>{showForm ? "Cancel" : "+ New Step"}</button>
      </div>
      <p style={{ fontSize: "0.82rem", color: "var(--ink-soft)", marginBottom: 18 }}>
        Each action type resolves a chain of steps by amount band — bands are half-open [min, max), the same for every company.
        A step with no named approvers can be decided by anyone holding the <b>approve</b> action on Legal Cases.
      </p>

      {error && <div style={{ color: "var(--st-risk)", marginBottom: 12 }}>{error}</div>}

      {showForm && (<div style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <select value={actionType} onChange={(e) => setActionType(e.target.value)} style={inputStyle}>
              {ACTION_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
            <input placeholder={isPercentAction(actionType) ? "Min %" : "Min QAR"} type="number" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} style={inputStyle}/>
            <input placeholder={isPercentAction(actionType) ? "Max % (blank = no cap)" : "Max QAR (blank = no cap)"} type="number" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} style={inputStyle}/>
            <input placeholder="Step #" type="number" min="1" value={sequence} onChange={(e) => setSequence(e.target.value)} style={inputStyle}/>
          </div>
          <div style={{ marginTop: 12, fontSize: "0.78rem", color: "var(--ink-soft)" }}>Approvers for this step (none ticked = any Management user):</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
            {managers.map((m) => (<label key={m.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.8rem", border: "1px solid var(--line-soft)", borderRadius: 8, padding: "5px 9px", cursor: "pointer" }}>
                <input type="checkbox" checked={approverUserIds.includes(m.id)} onChange={() => toggleApprover(m.id)}/>
                {m.name}
              </label>))}
            {managers.length === 0 && <span style={{ fontSize: "0.8rem", color: "var(--ink-faint)" }}>No user holds the approve action on Legal Cases yet.</span>}
          </div>
          <button onClick={create} disabled={saving} style={{ ...btnStyle, marginTop: 12 }}>Add Step</button>
        </div>)}

      {grouped.map((g) => (<div key={g.type} style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
          <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--line-soft)", fontWeight: 700, fontSize: "0.85rem" }}>
            {g.type.replace(/_/g, " ")}
          </div>
          {g.rules.length === 0 && <div style={{ padding: 14, color: "var(--ink-faint)", fontSize: "0.82rem" }}>No rules configured — this action cannot currently be requested.</div>}
          {g.rules.map((r) => (<div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "9px 16px", borderTop: "1px solid var(--line-soft)", fontSize: "0.82rem" }}>
              <span className="mono" style={{ color: "var(--ink-faint)", flex: "0 0 auto" }}>
                [{displayAmount(r.minAmount, g.type)} – {displayAmount(r.maxAmount, g.type)})
              </span>
              <span style={{ flex: 1 }}>Step {r.sequence}: {approversLabel(r.approverUserIds)}</span>
              <button onClick={() => remove(r.id)} style={deleteBtn}>Remove</button>
            </div>))}
        </div>))}
    </div>);
}
const inputStyle = { border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px", fontSize: "0.8rem", fontFamily: "inherit", width: "100%" };
const btnStyle = { background: "var(--brand)", color: "#fff", fontWeight: 600, fontSize: "0.82rem", padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer" };
const deleteBtn = { border: "1px solid var(--st-risk)", background: "transparent", color: "var(--st-risk)", fontWeight: 600, fontSize: "0.72rem", padding: "4px 10px", borderRadius: 7, cursor: "pointer" };
